import { useState } from "react";
import "./BranchesPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useBranches } from "../../../hooks/useBranches";
import {
  createBranchAsync,
  deleteBranchAsync,
  updateBranchAsync,
} from "../../../api/BranchApi";
import {
  Branch,
  CreateBranchRequest,
  UpdateBranchRequest,
} from "../../../models/BranchModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import BranchModal, {
  BranchFormValues,
  BranchModalMode,
} from "./BranchModal";
import Toast from "../../../components/Toast";

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const toUpdateRequest = (
  form: BranchFormValues,
): UpdateBranchRequest => ({
  name: form.name || undefined,
  phone: form.phone || undefined,
  email: form.email || undefined,
  description: form.description || undefined,
  address: form.address || undefined,
  city: form.city || undefined,
  area: form.area || undefined,
  latitude: form.latitude ? Number(form.latitude) : undefined,
  longitude: form.longitude ? Number(form.longitude) : undefined,
  isActive: form.isActive,
});

const BranchesPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateBranches = hasPrivilege(OrgForms.Branch, "canCreate");
  const canEditBranches = hasPrivilege(OrgForms.Branch, "canEdit");
  const canDeleteBranches = hasPrivilege(OrgForms.Branch, "canDelete");

  const {
    branches,
    loading,
    error,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  } = useBranches(organizationId);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<BranchModalMode>("create");
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const openModal = (mode: BranchModalMode, branch: Branch | null) => {
    setModalMode(mode);
    setSelectedBranch(branch);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (form: BranchFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const payload: CreateBranchRequest = {
          organizationId,
          name: form.name,
          phone: form.phone || undefined,
          email: form.email || undefined,
          description: form.description || undefined,
          address: form.address || undefined,
          city: form.city || undefined,
          area: form.area || undefined,
          latitude: form.latitude ? Number(form.latitude) : undefined,
          longitude: form.longitude ? Number(form.longitude) : undefined,
          isActive: form.isActive,
        };
        const res = await createBranchAsync(token, payload);
        if (res.success && res.result) {
          addLocal(res.result);
          closeModal();
          setToast({
            message: "Branch created successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to create branch",
            type: "error",
          });
        }
        return;
      }

      if (modalMode === "edit" && selectedBranch) {
        const res = await updateBranchAsync(
          token,
          selectedBranch.id,
          toUpdateRequest(form),
        );
        if (res.success && res.result) {
          updateLocal(res.result);
          closeModal();
          setToast({
            message: "Branch updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update branch",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (branch: Branch) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteBranchAsync(token, branch.id);
      if (res.success) {
        removeLocal(branch.id);
        setToast({
          message: "Branch deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: res.message || "Failed to delete branch",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to delete branch. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="branches-empty">Loading branches...</div>;
    }

    if (error) {
      return (
        <div className="branches-empty">
          <p>{error}</p>
          <button
            className="branches-btn branches-btn--secondary"
            onClick={reload}
          >
            Retry
          </button>
        </div>
      );
    }

    if (branches.length === 0) {
      return (
        <div className="branches-empty">
          No branches found. Click &quot;Add Branch&quot; to create one.
        </div>
      );
    }

    return (
      <div className="branches-table-wrap">
        <table className="branches-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>City</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((branch) => (
              <tr key={branch.id}>
                <td>
                  <div className="branches-name">
                    <span className="branches-avatar">
                      {branch.name.charAt(0).toUpperCase()}
                    </span>
                    {branch.name}
                  </div>
                </td>
                <td>{branch.city || "—"}</td>
                <td>{branch.phone || "—"}</td>
                <td>
                  <span
                    className={`branches-badge ${
                      branch.isActive ? "active" : "inactive"
                    }`}
                  >
                    {branch.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="branches-actions">
                  {canEditBranches && (
                    <button
                      className="branches-icon-btn branches-icon-btn--edit"
                      title="Edit"
                      aria-label={`Edit branch "${branch.name}"`}
                      onClick={() => openModal("edit", branch)}
                    >
                      <EditIcon />
                    </button>
                  )}
                  {canDeleteBranches && (
                    <button
                      className="branches-icon-btn branches-icon-btn--delete"
                      title="Delete"
                      aria-label={`Delete branch "${branch.name}"`}
                      onClick={() => {
                        if (window.confirm(`Delete "${branch.name}"?`)) {
                          handleDelete(branch);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="branches-page">
      <div className="branches-header">
        <div>
          <h2>Branches</h2>
          <p>Manage branches for {user?.activeOrganization?.name ?? "your organization"}</p>
        </div>
        {canCreateBranches && (
          <button
            className="branches-btn branches-btn--primary"
            onClick={() => openModal("create", null)}
          >
            + Add Branch
          </button>
        )}
      </div>

      {renderBody()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <BranchModal
        isOpen={modalOpen}
        mode={modalMode}
        branch={selectedBranch}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default BranchesPage;